import { Component, EventEmitter, inject, Input, Output, Type, ViewChild } from '@angular/core';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';
import { formatDate, NgClass, NgIf } from '@angular/common';
import { Audit } from '../../models/audit.model';
import { NavigationExtras, Router } from '@angular/router';
import { ConfirmationModel } from '../../models/confirmation.model';
import { ConfirmationButton } from '../../models/confirmation-button.model';
import { ConfirmationButtonType } from '../popups/confirmation/confirmation-button-type.enum';
import { take, tap } from 'rxjs';
import { PopupComponent } from '../popups/popup/popup.component';
import { ConfirmationComponent } from '../popups/confirmation/confirmation.component';
import { QuestionService } from '../../services/question.service';
import { AssignmentService } from '../../services/assignment.service';
import { AuditState } from '../../models/audit-state.enum';
import { AuditType } from '../../models/audit-type.enum';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-main-card',
  standalone: true,
  imports: [
    SvgIconComponent,
    NgClass,
    NgIf,
    PopupComponent,
    TranslocoDirective,
    TranslocoPipe
  ],
  templateUrl: './main-card.component.html',
  styleUrl: './main-card.component.scss'
})
export class MainCardComponent {
  @Input() public audit: Audit = new Audit();
  @Output() public onDelete: EventEmitter<string> = new EventEmitter<string>();

  protected highlightForDelete: boolean = false;
  protected popupVisible: boolean = false;
  protected dynamicComponent: Type<any> = Object;
  protected popupData: ConfirmationModel = new ConfirmationModel();
  protected readonly auditState = AuditState;
  protected readonly auditType = AuditType;

  @ViewChild(PopupComponent) popupComponent!: PopupComponent;

  private startX: number | null = null;
  private stopClick: boolean = false;
  private navigationExtras: NavigationExtras = {
    state: {
      audit: new Audit()
    }
  };

  private router = inject(Router);
  private questionService = inject(QuestionService);
  private assignmentService = inject(AssignmentService);
  private translocoService = inject(TranslocoService);

  protected start(event: MouseEvent | TouchEvent) {
    this.startX = event instanceof MouseEvent ? event.clientX : event.changedTouches[0].clientX;
  }

  protected up(event: MouseEvent | TouchEvent) {
    let endX = event instanceof MouseEvent ? event.clientX : event.changedTouches[0].clientX;

    if (this.startX && (endX - this.startX) > 50) {
      this.highlightForDelete = false;
      this.stopClick = true;
      this.startX = null;
    } else if (this.startX && (endX - this.startX) < -50) {
      this.highlightForDelete = true;
      this.stopClick = true;
      this.startX = null;
    } else {
      this.stopClick = this.highlightForDelete;
    }
  }

  protected click() {
    if (!this.stopClick) {
      if (this.audit.state === AuditState.Finished) {
        this.navigationExtras = {
          state: {
            audit: this.audit
          }
        };
        this.navigate('audit-summary', this.navigationExtras)
      } else if ((!this.assignmentService.auditInProgress().length || this.assignmentService.auditInProgress()[0].id === this.audit.id) && this.isToday()) {
          const id = this.audit.id
          this.assignmentService.getTaskLayoutId(id).pipe(
            tap((taskLayoutId) => this.router.navigate(['audit', taskLayoutId]))
          ).subscribe();
      } else if (this.isToday()){
        this.openIncompletePopup();
      } else {
        this.openNotYetPopup()
      }
    }
  }

  protected displayDueDate(): string {
    if (this.isTomorrow()) {
      return 'MAIN-CARD.DUE-DATE.TOMORROW';
    }
    if (this.isToday()) {
      return 'MAIN-CARD.DUE-DATE.TODAY';
    }
    if (this.isYesterday()) {
      return 'MAIN-CARD.DUE-DATE.YESTERDAY';
    }
    return formatDate(this.audit.dueDate, 'yyyy-MM-dd', 'en-US') ?? '';
  }

  protected setClass(type: AuditType): string {
    switch (type) {
      case AuditType.FiveS:
        return 'five-s';
      case AuditType.Ehs:
        return 'ehs';
      case AuditType.Qmm:
        return 'qmm';
      case AuditType.Es:
        return 'es';
      default:
        return '';
    }
  }

  protected delete() {
    this.openDeletePopup()
  }


  protected isToday(): boolean {
    return this.audit.dueDate.getFullYear() === this.startOfToday().getFullYear()
      && this.audit.dueDate.getMonth() === this.startOfToday().getMonth()
      && this.audit.dueDate.getDate() === this.startOfToday().getDate()

  }

  protected isOverdue(): boolean {
    return this.audit.dueDate < this.startOfToday()
  }

  protected getPrevalenceKey(frequency: string): string {
    const normalized = (frequency ?? '').toUpperCase();

    const validKeys: Record<string, string> = {
      DAILY: 'COMMON.LABEL.DAILY',
      WEEKLY: 'COMMON.LABEL.WEEKLY',
      MONTHLY: 'COMMON.LABEL.MONTHLY',
      QUARTERLY: 'COMMON.LABEL.QUARTERLY'
    };

    return validKeys[normalized] ?? 'COMMON.LABEL.UNKNOWN';
  }

  private openDeletePopup() {
    this.dynamicComponent = ConfirmationComponent;
    this.popupVisible = !this.popupVisible;
    this.popupData = new ConfirmationModel({
      imagePath: 'assets/trash-big.svg',
      prompt: this.translocoService.translate('POPUPS.DELETE.TITLE'),
      hint: this.translocoService.translate('POPUPS.DELETE.HINT'),
      buttons: [
        new ConfirmationButton({
          text: this.translocoService.translate('COMMON.LABEL.CANCEL'),
          type: ConfirmationButtonType.Cancel
        }),
        new ConfirmationButton({
          text: this.translocoService.translate('POPUPS.DELETE.DELETE'),
          type: ConfirmationButtonType.SecondaryConfirm
        })]
    });
    this.popupComponent.confirmed.pipe(take(1)).subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.questionService.selectedId.next('');
        this.onDelete.emit(this.audit.id)
      }
    });
  }

  private openIncompletePopup() {
    this.dynamicComponent = ConfirmationComponent;
    this.popupVisible = !this.popupVisible;
    this.popupData = new ConfirmationModel({
      imagePath: 'assets/incomplete.svg',
      prompt: this.translocoService.translate('POPUPS.IN-PROGRESS.TITLE'),
      hint: this.translocoService.translate('POPUPS.IN-PROGRESS.HINT'),
      buttons: [
        new ConfirmationButton({
          text: this.translocoService.translate('COMMON.LABEL.OKAY'),
          type: ConfirmationButtonType.Confirm
        })]
    });
  }

  private openNotYetPopup() {
    this.dynamicComponent = ConfirmationComponent;
    this.popupVisible = !this.popupVisible;
    this.popupData = new ConfirmationModel({
      imagePath: 'assets/NOT-YET.svg',
      prompt: this.translocoService.translate('POPUPS.NOT-YET.TITLE'),
      hint: this.translocoService.translate('POPUPS.NOT-YET.HINT'),
      buttons: [
        new ConfirmationButton({
          text: this.translocoService.translate('COMMON.LABEL.OKAY'),
          type: ConfirmationButtonType.Confirm
        })]
    });
  }

  private isYesterday(): boolean {
    return this.audit.dueDate.getDate() === this.startOfYesterday().getDate()
  }

  private isTomorrow(): boolean {
    return this.audit.dueDate.getFullYear() === this.startOfTomorrow().getFullYear()
      && this.audit.dueDate.getMonth() === this.startOfTomorrow().getMonth()
      && this.audit.dueDate.getDate() === this.startOfTomorrow().getDate()
  }

  private navigate(route: string, data?: NavigationExtras): void {
    this.router.navigate([route], data).then();
  }

  private startOfTheDay(dayOffset: number): Date {
    return new Date(new Date(new Date().setDate(new Date().getDate() + dayOffset)).setHours(0, 0, 0, 0));
  }

  private startOfToday(): Date {
    return this.startOfTheDay(0);
  }

  private startOfYesterday(): Date {
    return this.startOfTheDay(-1);
  }

  private startOfTomorrow(): Date {
    return this.startOfTheDay(1);
  }
}
